<template>
    <div
        class="flex cursor-pointer items-center gap-4 border p-4 hover:border-primary-500"
        :class="selected ? 'border-primary-800' : ''"
        :style="[
            selected
                ? 'box-shadow: 0 0 0 1px rgb(var(--color-primary-500));'
                : ''
        ]"
        @click="$emit('select')"
    >
        <radio-button
            :model-value="selected ? courseData.id : null"
            :input-id="courseData.id"
            name="course"
            :value="courseData.id"
            @update:model-value="$event ? $emit('select') : void 0"
        />

        <div class="flex-1 space-y-1">
            <div class="flex flex-col gap-2 md:flex-row md:gap-4">
                <div class="flex-1">
                    <div
                        v-if="courseData.bookingPeriod.courseStarted"
                        class="text-sm font-medium text-warning-700"
                    >
                        {{ t('purchaseContract.bookingCourseStarted') }}
                    </div>
                    <div class="text-lg font-semibold">
                        {{ courseData.publicTitle }}
                    </div>
                    <div
                        v-if="courseData.subtitle !== ''"
                        class="pb-2 text-sm"
                    >
                        {{ courseData.subtitle }}
                    </div>
                </div>
                <div class="space-y-1 md:max-w-[50%] md:text-right">
                    <span
                        :class="{
                            'font-medium text-danger-600':
                                courseAvailabilityMessage.theme === 'danger',
                            'font-medium text-success-600':
                                courseAvailabilityMessage.theme === 'success',
                            italic: courseAvailabilityMessage.theme === 'base'
                        }"
                    >
                        <i
                            v-if="courseAvailabilityMessage.isOpeningMessage"
                            class="pi pi-calendar mr-1 text-sm not-italic"
                            aria-hidden="true"
                        />
                        {{ courseAvailabilityMessage.message }}
                    </span>
                    <div
                        v-if="
                            courseAvailabilityMessage.isOpeningMessage &&
                            courseData.bookingPeriod.explanation
                        "
                        class="whitespace-pre-line text-sm text-gray-600"
                    >
                        {{ courseData.bookingPeriod.explanation }}
                    </div>
                </div>
            </div>
            <div class="font-medium">{{ t('purchaseContract.sessions') }}</div>
            <div
                class="relative"
                :class="
                    hasOverflowingContent
                        ? moreShown
                            ? ''
                            : 'max-h-[100px] overflow-hidden'
                        : ''
                "
            >
                <div ref="sessionsListDiv">
                    <ul class="list-disc pl-6">
                        <li
                            v-for="session in courseData.sessions"
                            :key="session.id"
                        >
                            {{
                                t('purchaseContract.bookingSessionSequence', {
                                    sequence: session.sessionSequence,
                                    count: session.sessionCount
                                })
                            }}
                            &mdash;
                            {{
                                formatRange(
                                    parseLocal(session.startDateLocal),
                                    parseLocal(session.endDateLocal)
                                )
                            }}
                            <span
                                v-if="
                                    showInstructors &&
                                    session.instructorText !== null &&
                                    session.instructorText !== ''
                                "
                                class="italic"
                                >&mdash;&nbsp;{{ session.instructorText }}</span
                            >
                            <div
                                v-if="session.subtitle !== ''"
                                class="pb-2 text-sm"
                            >
                                {{ session.subtitle }}
                            </div>
                        </li>
                    </ul>
                </div>
                <div
                    v-if="hasOverflowingContent && !moreShown"
                    class="absolute inset-x-0 -bottom-1 h-20 bg-gradient-to-b from-white/10 to-white blur-sm"
                />
            </div>
            <prime-button
                v-if="hasOverflowingContent && !moreShown"
                text
                severity="secondary"
                @click="moreShown = true"
            >
                {{ t('showMore') }}
            </prime-button>
            <prime-button
                v-if="moreShown"
                text
                severity="secondary"
                @click="moreShown = false"
            >
                {{ t('showLess') }}
            </prime-button>
        </div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const CourseDisplayCourseFragment = graphql(`
    fragment CourseDisplayCourseFragment on Course {
        id
        publicTitle
        subtitle
        capacityResult(filter: $capacityResultFilter) {
            enrollments
            overlappedBookedSessions
            contracts
            isWaitlistFull
        }
        sessions {
            id
            sessionSequence
            sessionCount
            startDateLocal
            endDateLocal
            instructorText
            subtitle
        }
        bookingStatus
        bookingStatusAvailable(filter: $bookingStatusFilter) {
            customerName
            session {
                sessionId
                startDate
            }
            overrideCapacityConstraints
        }
        bookingPeriod(filter: $bookingPeriodFilter) {
            status
            opensAt
            courseStarted
            explanation(language: $language)
        }
    }
`)
</script>

<script setup lang="ts">
import RadioButton from 'primevue/radiobutton'
import { formatRange, parseLocal } from '~/composables/support/localization'
import { computed, ref } from 'vue'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import PrimeButton from 'primevue/button'
import { useElementSize } from '@vueuse/core'
import { useCourseAvailabilityMessage } from '~/components/PurchaseContract/Program/helpers/use-course-availability-message'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    course: FragmentType<typeof CourseDisplayCourseFragment>
    selected: boolean
    availabilityThreshold: number | null
    showInstructors: boolean
    waitlistEnabled: boolean
}>()
const { t } = useI18n({ useScope: 'global' })

defineEmits<{
    (e: 'select'): void
}>()

const courseData = computed(() =>
    getFragmentData(CourseDisplayCourseFragment, props.course)
)

const courseAvailabilityMessage = useCourseAvailabilityMessage(
    computed(() => props.waitlistEnabled),
    computed(() => props.availabilityThreshold),
    computed(() => {
        return {
            bookingStatus: courseData.value.bookingStatus,
            bookingStatusAvailable: courseData.value.bookingStatusAvailable,
            bookingPeriod: courseData.value.bookingPeriod,
            capacityResult: {
                isWaitlistFull: courseData.value.capacityResult.isWaitlistFull,
                overlappedBookedSessions:
                    courseData.value.capacityResult.overlappedBookedSessions,
                contracts: courseData.value.capacityResult.contracts,
                enrollments: courseData.value.capacityResult.enrollments
            }
        }
    })
)

const moreShown = ref(false)
const sessionsListDiv = ref<HTMLDivElement>()
const { height } = useElementSize(sessionsListDiv)
const hasOverflowingContent = computed(() => {
    return sessionsListDiv.value === undefined || height.value > 110
})
</script>
